import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Breadcrumbs, IconButton, Link, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import { useTheme } from "../../contexts/ThemeContext";
import {
  PAGE_ROOT,
  getPageLabelKey,
  getStaticBreadcrumbTrail,
} from "../../navigation/pageBreadcrumbs";
import { getMm } from "../../theme/modernMaterialEnterprise";

const NUNITO = "'Plus Jakarta Sans', sans-serif";

const crumbBoxSx = (compact) => ({
  display: { xs: "none", sm: "block" },
  width: "100%",
  maxWidth: 1600,
  mx: "auto",
  boxSizing: "border-box",
  px: compact ? { xs: 1.5, sm: 2 } : { xs: 0, sm: 0 },
  pt: compact ? 1 : { xs: 1.5, sm: 2 },
  pb: compact ? 0.5 : 1,
  fontFamily: NUNITO,
});

const currentCrumbSx = (compact, currentColor) => ({
  fontSize: compact ? "0.8125rem" : "0.875rem",
  fontWeight: 600,
  color: currentColor,
  fontFamily: NUNITO,
  display: "flex",
  alignItems: "center",
  gap: 0.5,
});

/**
 * Breadcrumb bar for in-app navigation on logged-in pages (not LandingPage).
 *
 * @param {object} props
 * @param {string} props.selectedPage
 * @param {(page: string) => void} props.onNavigate
 * @param {string[]} [props.trail] — optional page id trail (root → current); defaults to static hierarchy
 * @param {Record<string, string>} [props.dynamicLabels] — override labels, e.g. { NewsDetail: "Article title" }
 * @param {string} [props.suffixLabel] — extra final segment (e.g. active Economy or POS tab)
 * @param {boolean} [props.compact] — tighter padding (kiosk / fullscreen pages)
 */
export default function PageBreadcrumbs({
  selectedPage,
  onNavigate,
  trail: trailOverride,
  dynamicLabels = {},
  suffixLabel = null,
  compact = false,
}) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const trail = useMemo(
    () => trailOverride ?? getStaticBreadcrumbTrail(selectedPage),
    [trailOverride, selectedPage]
  );

  if (!selectedPage || selectedPage === PAGE_ROOT) return null;

  if (trail.length <= 1 && !suffixLabel) return null;

  const linkColor = isDarkMode ? "#93C5FD" : "#085ac0";
  const currentColor = getMm(isDarkMode).onSurface;
  const separatorColor = getMm(isDarkMode).onSurfaceVariant;

  const labelFor = (pageId) => {
    if (dynamicLabels[pageId]) return dynamicLabels[pageId];
    const key = getPageLabelKey(pageId);
    const translated = t(key);
    return translated !== key ? translated : pageId;
  };

  const parentPageId = trail.length >= 2 ? trail[trail.length - 2] : null;
  const currentPageId = trail[trail.length - 1];
  const currentPageLabel = suffixLabel || labelFor(currentPageId);
  const parentPageLabel = parentPageId ? labelFor(parentPageId) : null;

  const handleMobileBack = () => {
    if (parentPageId) onNavigate(parentPageId);
  };

  return (
    <>
      {parentPageId ? (
        <Box
          sx={{
            display: { xs: "block", sm: "none" },
            width: "100%",
            maxWidth: 1600,
            mx: "auto",
            boxSizing: "border-box",
            px: compact ? 1.5 : { xs: 0, sm: 0 },
            pt: compact ? 1 : { xs: 0.5, sm: 0 },
            pb: { xs: 1.5, sm: 0 },
            fontFamily: NUNITO,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: 1.25,
              borderRadius: "8px",
              border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
              backgroundColor: getMm(isDarkMode).surfaceLowest,
              boxShadow: "none",
            }}
          >
            <IconButton
              type="button"
              onClick={handleMobileBack}
              aria-label={t("mobile_back_to", { page: parentPageLabel })}
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: "8px",
                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                backgroundColor: getMm(isDarkMode).surfaceLow,
                color: getMm(isDarkMode).onSurface,
                "&:hover": {
                  backgroundColor: getMm(isDarkMode).surfaceContainer,
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 22 }} />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1, py: 0.25 }}>
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: getMm(isDarkMode).onSurface,
                  fontFamily: NUNITO,
                  lineHeight: 1.35,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t("mobile_back_to", { page: parentPageLabel })}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : null}

    <Box sx={crumbBoxSx(compact)} aria-label={t("breadcrumb_navigation")}>
      <Breadcrumbs
        separator={
          <NavigateNextIcon
            sx={{ fontSize: "1rem", color: separatorColor, mx: 0.25 }}
          />
        }
        sx={{
          "& .MuiBreadcrumbs-ol": { flexWrap: "wrap" },
          "& .MuiBreadcrumbs-li": { maxWidth: "100%" },
        }}
      >
        {trail.map((pageId, index) => {
          const isLast = !suffixLabel && index === trail.length - 1;
          const label = labelFor(pageId);
          const isHome = pageId === PAGE_ROOT;

          if (isLast) {
            return (
              <Typography key={pageId} sx={currentCrumbSx(compact, currentColor)}>
                {isHome && (
                  <HomeIcon sx={{ fontSize: "1rem", opacity: 0.85 }} />
                )}
                {label}
              </Typography>
            );
          }

          return (
            <Link
              key={pageId}
              component="button"
              type="button"
              underline="hover"
              onClick={() => onNavigate(pageId)}
              sx={{
                fontSize: compact ? "0.8125rem" : "0.875rem",
                fontWeight: 500,
                color: linkColor,
                fontFamily: NUNITO,
                cursor: "pointer",
                border: "none",
                background: "none",
                p: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                textAlign: "left",
                "&:hover": { color: isDarkMode ? "#BFDBFE" : "#064299" },
              }}
            >
              {isHome && (
                <HomeIcon sx={{ fontSize: "1rem", opacity: 0.9 }} />
              )}
              {label}
            </Link>
          );
        })}
        {suffixLabel ? (
          <Typography key="__suffix__" sx={currentCrumbSx(compact, currentColor)}>
            {suffixLabel}
          </Typography>
        ) : null}
      </Breadcrumbs>
    </Box>
    </>
  );
}
